import styled from 'styled-components';

import Title from '@/components/Module/Title';
import useViewport from '@/hooks/useViewport';

export type TermsProps = {};

export const Paragraph = styled.div`
  padding-bottom: 2rem;
`;
export const ListItem = styled.li`
  padding-bottom: 2rem;
`;

const TermsContents = (props: TermsProps) => {
  const {} = props;

  return (
    <div className={`px-[24px]`}>
      <Paragraph>
        <Title>Terms of Use</Title>
      </Paragraph>

      <Paragraph className={`mb-[24px] text-b2 font-normal text-gray-100`}>
        Last Updated: May 12, 2022
      </Paragraph>
      <Paragraph className={`text-b2 font-normal text-gray-200`}>
        <Paragraph>
          These Terms of Service (these “<strong>Terms</strong>”) describe your
          rights and responsibilities when accessing and using the Someday.fm
          website (currently available at{` `}
          <a href="https://www.someday.fm">https://www.someday.fm</a>) and any
          and all related products and services (collectively, the “
          <strong>Service</strong>”) offered by GigFinesse, Inc. d/b/a{` `}
          Someday.fm (“
          <strong>Someday</strong>,” “<strong>we</strong>,” “
          <strong>our</strong>,” or “<strong>us</strong>”). By accessing or
          using the Service, or by clicking a button or checking a box marked “I
          Agree” (or something similar), you signify that you have read,
          understood, and agree to be bound by these Terms, whether or not you
          are a registered user of the Service. Someday reserves the right to
          modify these Terms and will provide notice of modifications as
          described below.
        </Paragraph>
        <Paragraph>
          Please read these Terms carefully to ensure that you understand each
          provision. These Terms contain a mandatory mutual individual
          arbitration provision in Section 14.2 (the “
          <strong>Arbitration Agreement</strong>”) and a mutual class
          action/jury trial waiver provision in Section 14.3 (the “
          <strong>Class Action/Jury Trial Waiver</strong>”) that require,
          subject only to the specified exceptions in Sections 14.2 and 14.3 or
          unless you opt out pursuant to the instructions in Section 14.2, the
          exclusive use of final and binding arbitration on an individual basis
          only to resolve disputes, rather than jury trials or class,
          collective, private attorney general, or representative actions or
          proceedings.
        </Paragraph>
        <ol>
          <ListItem>
            <Paragraph>
              <strong>Our Service</strong>. Someday is a disclosed ticketing
              agency selling tickets via the Service (“
              <strong>Tickets</strong>”) on behalf of event organizers, artists
              and venues of music, entertainment, sports and similar events
              (each, an “<strong>Event</strong>”). These Terms apply those
              purchasing Tickets for events (“<strong>Consumers</strong>
              ”), and anyone else who accesses or visits the Service. Someday
              retains sole discretion to prioritize, categorize, highlight, and
              otherwise modify the method of Consumers discovery of Events,
              including through search results and tagging, in order to improve
              service quality and for other purposes.
            </Paragraph>
            <ul>
              <ListItem>
                <Paragraph>
                  1.1. <strong>Eligibility.</strong> This is a contract between
                  you (“<strong>you</strong>”, “<strong>your</strong>
                  ”, or “<strong>User</strong>”) and Someday. You must read and
                  agree to the terms and conditions of these Terms before using
                  the Service. If you do not agree, you may not use the Service.
                  You may use the Service only if you can form a binding
                  contract with Someday, and only in compliance with these Terms
                  and all applicable local, state, federal, national, and
                  international laws, rules and regulations. You must be at
                  least 18 years of age to use the Services. If you are 13 or
                  older, you may only use the Services under the supervisions of
                  a parent or legal guardian who manages your use and/or
                  account. The Service is not available to any Users previously
                  removed from the Service by Someday.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  1.2. <strong>Limited License.</strong> Subject to the terms
                  and conditions of these Terms, you are hereby granted a
                  non-exclusive, limited, non-transferable, freely revocable
                  license to use the Service for your personal use, in each
                  case, as permitted by the features of the Service. Someday
                  reserves all rights not expressly granted herein in and to the
                  Service and the Someday Content (as defined below). Someday
                  may terminate the license granted in this section at any time
                  for any reason or no reason.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  1.3. <strong>User Accounts</strong> You may be required to
                  register an account on the Service (your “
                  <strong>User Account</strong>”) in order to gain access to
                  certain of the Service’s functionalities. We may maintain
                  different types of User Accounts for different types of Users.
                  If you open a User Account on behalf of a company,
                  organization, or other entity then (i) “you” includes you and
                  that entity, and (ii) you represent and warrant that you are
                  an authorized representative of the entity, with the authority
                  to bind the entity to these Terms, and that you agree to these
                  Terms on the entity’s behalf. By connecting to Someday with a
                  third-party service, you give us permission to access and use
                  your information from that service as permitted by that
                  service, and to store your log-in credentials for that
                  service.
                </Paragraph>
                <Paragraph>
                  You may never use another User’s User Account without
                  permission. When creating your User Account, you must provide
                  accurate and complete information, and you must keep this
                  information up to date. You are solely responsible for the
                  activity that occurs on your User Account, and you must keep
                  your User Account password secure. You must notify Someday
                  immediately of any breach of security or unauthorized use of
                  your User Account. Someday will not be liable for any losses
                  caused by any unauthorized use of your User Account.
                </Paragraph>
                <Paragraph>
                  You may control your User Account and how you interact with
                  the Service by changing the settings in your settings page.
                  Please refer to Someday’s Privacy Policy for more information
                  about communications and choices the User has in connection
                  with those communications.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  1.4. <strong>Relationship with Someday</strong>
                </Paragraph>
                <ul>
                  <ListItem>
                    <Paragraph>
                      (a){` `}
                      <strong>
                        <em>No Warranties</em>
                      </strong>
                    </Paragraph>
                    <Paragraph>
                      Someday does not operate the venues used in connection
                      with the Events and does not employ individuals or engage
                      entities to provide Events. Someday operates as an online
                      marketplace that connects Consumers with Events.
                      Regardless of whether a particular artist or venue appears
                      as “vetted,” “verified” or something similar, the Service
                      is intended as an information resource and not an advisory
                      service; it is your responsibility to determine the
                      quality and credibility of information obtained, as well
                      as the reasonableness of relying on such information for a
                      particular use.
                    </Paragraph>
                    <Paragraph>
                      Someday does not make any representations about, or
                      guarantee the truth or accuracy of, any Feedback, and does
                      not guarantee any User’s suitability for any purpose
                      (regardless of whether a particular Event appears as
                      “vetted,” “verified” or something similar).
                    </Paragraph>
                    <Paragraph>
                      Users hereby acknowledge that Someday does not supervise,
                      scope, direct, control or monitor the venues, Events or
                      Tickets or anything else an Event may offer or provide
                      through or in connection with the Service and expressly
                      disclaims (to the extent permitted by law) any
                      responsibility and liability for the Events and anything
                      else a venue may offer or provide in any manner in
                      connection with the Tickets, including but not limited to,
                      any warranty or condition of good and workmanlike
                      services, warranty or condition of quality or fitness for
                      a particular purpose, or compliance with any law, statute,
                      ordinance, regulation, or code. You hereby irrevocably and
                      forever waive any and all claims you may have with respect
                      to the foregoing.
                    </Paragraph>
                    <Paragraph>
                      Someday makes no representations about and does not
                      guarantee, and you agree not to hold Someday responsible
                      for: the quality, safety, or legality of any services
                      performed by venue or at the event; a venue’s failure to
                      provide an Event or any other promised service, or the
                      ability of an Event to deliver services; the ability of an
                      Eventto pay sums due in connection with any services
                      provided through or in connection with the Service. You
                      hereby irrevocably and forever waive any and all claims
                      you may have with respect to the foregoing.
                    </Paragraph>
                  </ListItem>
                </ul>
              </ListItem>
              <ListItem>
                <Paragraph>
                  1.5. <strong>Changes to the Service.</strong> We may, without
                  prior notice, change the Service; stop providing the Service
                  or features of the Service, to you or to Users generally; or
                  create usage limits for the Service. We may permanently or
                  temporarily terminate or suspend your User Account and/or
                  access to the Service, without notice and without liability,
                  for any reason, including if, in our sole determination, you
                  violate any provision of these Terms, or for no reason. Upon
                  termination for any reason or no reason, you continue to be
                  bound by these Terms.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  1.6. <strong>Service Location.</strong> The Service is
                  controlled and operated from facilities in the United States.
                  Someday makes no representations that the Service is available
                  for use in other locations. Those who access or use the
                  Service from other jurisdictions do so at their own volition
                  and are entirely responsible for compliance with all
                  applicable United States and local laws and regulations,
                  including but not limited to, export and import regulations.
                  You may not use the Service if you are a resident of a country
                  embargoed by the United States, or are a foreign person or
                  entity blocked or denied by the United States government.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  1.7. <strong>Feedback</strong>. For the purposes of these
                  Terms, “<strong>Feedback</strong>” means composite or compiled
                  feedback about and from Users. You acknowledge, agree and
                  understand that Feedback does not constitute and will not be
                  construed as an introduction to, or endorsement or
                  recommendation of, any User by Someday, and that Someday
                  provides Feedback solely for the convenience of Users. You
                  further acknowledge, agree and understand that Someday will
                  make Feedback available to other Users on or through the
                  Service, and may make Feedback available on Events Profiles.
                  Someday provides Feedback as a means through which Users can
                  share their opinions of other Users publicly, and Someday does
                  not monitor, contribute to or censor these opinions. You agree
                  not to use the Feedback to make any employment, credit, credit
                  valuation, underwriting, or other similar decision about any
                  other User.
                </Paragraph>
              </ListItem>
            </ul>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Our Proprietary Rights</strong>
              <br />
              Except for User Content you create, the Service and all materials
              therein or transferred thereby, including, without limitation,
              software, images, text, graphics, illustrations, logos, patents,
              trademarks, service marks, copyrights, photographs, audio, videos,
              music, and User Content belonging to other Users (the “
              <strong>Someday Content</strong>”), and all Intellectual Property
              Rights related thereto, are the exclusive property of Someday and
              its licensors (including other Users who post User Content to the
              Service). Except as explicitly provided herein, nothing in these
              Terms shall be deemed to create a license in or under any such
              Intellectual Property Rights, and you agree not to sell, license,
              rent, modify, distribute, copy, reproduce, transmit, publicly
              display, publicly perform, publish, adapt, edit or create
              derivative works from any Someday Content. Use of the Someday
              Content for any purpose not expressly permitted by these Terms is
              strictly prohibited.
            </Paragraph>
            <Paragraph>
              You may choose to, or we may invite you to, submit comments or
              ideas about the Service, including, without limitation, about how
              to improve the Service (“<strong>Ideas</strong>”). By submitting
              any Idea, you agree that your disclosure is gratuitous,
              unsolicited and without restriction and will not place Someday
              under any fiduciary or other obligation, and that we are free to
              use the Idea without any additional compensation to you, and/or to
              disclose the Idea on a non-confidential basis or otherwise to
              anyone. You further acknowledge that, by acceptance of your
              submission, Someday does not waive any rights to use similar or
              related ideas previously known to Someday, or developed by its
              employees, or obtained from sources other than you.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Paid Services</strong>
            </Paragraph>
            <ul>
              <ListItem>
                <Paragraph>
                  3.1. <strong>General.</strong> Certain aspects of the Service
                  may be provided for a fee or other charge. If you elect to use
                  paid aspects of the Service, you agree to our
                  <a href="https://support.someday.fm/en/articles/6211198-purchase-terms-and-policies">
                    Purchase Terms and Policies
                  </a>
                  , which are hereby incorporated by reference herein, as we may
                  update them from time to time. For any transaction you make
                  through the Service, you agree that a certain percentage of
                  the total transaction amount, as described in the Purchase
                  Terms and Policies, may be paid to or retained by Someday, and
                  you acknowledge that we may update this percentage and/or our
                  compensation scheme from time to time. You also acknowledge
                  that any transaction made through the Service may be subject
                  to Third-Party Fees (as defined below), as described in the
                  Pricing and Payment Terms. Someday may add new services for
                  additional fees and charges, and may add or amend fees and
                  charges for existing services, at any time, in its sole
                  discretion. Any change to our Purchase Terms and Policies
                  shall become effective in the billing cycle following notice
                  of such change to you as provided in these Terms. Unless
                  otherwise expressly agreed by Someday in writing, Consumer
                  will not pay an Event directly for any services provided
                  through or in connection with the Service.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  3.2. <strong>Payments by Users</strong>
                </Paragraph>
                <ul>
                  <ListItem>
                    <Paragraph>
                      (a){` `}
                      <strong>
                        <em>General</em>
                      </strong>
                    </Paragraph>
                    <Paragraph>
                      Consumer agrees to pay Someday all Ticket Fees on the
                      terms set forth herein and in the Pricing and Payment
                      Terms. Someday will process and collect the Fees in
                      accordance with these Terms and the Pricing and Payment
                      Terms.
                    </Paragraph>
                    <Paragraph>
                      Upon Consumer’s payment of Ticket Fees to Someday,
                      Consumer’s payment obligation for the Event is
                      extinguished in an amount equal to the payment for Ticket
                      Fees made to Someday, and Someday is responsible for
                      remitting such Ticket Fees to any other involved parties
                      (less any applicable Third-Party Fees).
                    </Paragraph>
                    <Paragraph>
                      In connection with payments of Ticket Fees, Consumer will
                      be asked to provide customary billing information such as
                      name, billing address, and credit card information to
                      Someday or its third-party payment processor (which may
                      include, without limitation, Apple or Google). Consumer
                      hereby authorizes the collection of such amounts by
                      charging the Consumer’s designated payment method or
                      credit or debit card, either directly by Someday or
                      indirectly via a third-party online payment processor or
                      by one of the payment methods described on the Service. If
                      Consumer is directed to Someday’s third-party payment
                      processor, Consumer will be subject to terms and
                      conditions governing the use of that third party’s service
                      and to that third party’s personal information collection
                      practices. Please review such terms and conditions and
                      privacy policy before using such third party’s services.
                      Consumer acknowledges and understands that third-party
                      payment processors may collect and retain Third-Party Fees
                      whenever Consumer pays Ticket Fees.
                    </Paragraph>
                    <Paragraph>
                      Ticket Fees may vary and are non-refundable. Consumers who
                      believe they have been improperly charged and require a
                      refund are asked to contact Someday at{` `}
                      <a href="mailto:support@someday.fm">support@someday.fm</a>
                      .
                    </Paragraph>
                  </ListItem>
                </ul>
              </ListItem>
              <ListItem>
                <Paragraph>
                  3.3. <strong>Payment Information; Taxes.</strong> All
                  information that you provide in connection with a monetary
                  transaction interaction with the Service must be accurate,
                  complete, and current. You agree to pay all charges incurred
                  by users of your credit card, debit card, or other payment
                  method used in connection with a monetary transaction
                  interaction with the Service at the prices in effect when such
                  charges are incurred. You will pay all applicable taxes, if
                  any, relating to any monetary transaction interactions with
                  the Service.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  3.4. <strong>California Residents.</strong> The provider of
                  services is set forth herein. If you are a California
                  resident, in accordance with Cal. Civ. Code §1789.3, you may
                  report complaints to the Complaint Assistance Unit of the
                  Division of Consumer Services of the California Department of
                  Consumer Affairs by contacting them in writing at 1625 North
                  Market Blvd., Suite N 112 Sacramento, CA 95834, or by
                  telephone at (800) 952-5210 or (916) 445-1254.
                </Paragraph>
              </ListItem>
            </ul>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>No Professional Advice.</strong> Information provided
              through the Service (for example, information related to the
              quality of a particular Event) is for informational purposes only
              and should not be construed as professional advice. No action
              should be taken based upon any information contained in the
              Service. You should seek independent professional advice from a
              person who is licensed and/or qualified in the applicable area.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Technical Restrictions</strong>
            </Paragraph>
            <Paragraph>
              You agree not to engage in any of the following prohibited
              activities: (i) copying, distributing, or disclosing any part of
              the Service in any medium, including, without limitation, by any
              automated or non-automated “scraping”; (ii) using any automated
              system, including, without limitation, “robots,” “spiders,”
              “offline readers,” etc., to access the Service in a manner that
              sends more request messages to the Someday servers than a human
              can reasonably produce in the same period of time by using a
              conventional on-line web browser (except that Someday grants the
              operators of public search engines revocable permission to use
              spiders to copy publicly available materials from{` `}
              <a href="https://www.someday.fm">https://www.someday.fm</a> (or
              any successor URL) for the sole purpose of and solely to the
              extent necessary for creating publicly available searchable
              indices of the materials, but not caches or archives of such
              materials); (iii) transmitting spam, chain letters, or other
              unsolicited email or otherwise violating any anti-spam, consumer
              protection or privacy law, rule or regulation by using another
              User’s email address; (iv) attempting to interfere with,
              compromise the system integrity or security or decipher any
              transmissions to or from the servers running the Service; (v)
              taking any action that imposes, or may impose at our sole
              discretion an unreasonable or disproportionately large load on our
              infrastructure; (vi) uploading invalid data, viruses, worms, or
              other software agents through the Service; (vii) collecting or
              harvesting any personal information, including account names, from
              the Service; (viii) using the Service for any commercial
              solicitation purposes; (ix) impersonating another person or
              otherwise misrepresenting your affiliation with a person or
              entity, conducting fraud, hiding or attempting to hide your
              identity; (x) interfering with the proper working of the Service;
              (xi) accessing any content on the Service through any technology
              or means other than those provided or authorized by the Service;
              (xii) bypassing the measures we may use to prevent or restrict
              access to the Service, including, without limitation, features
              that prevent or restrict use or copying of any content or enforce
              limitations on the use of the Service or the content therein; or
              (xiii) accessing any audiovisual content that may be available on
              the Service in any manner other than that permitted by the
              functionality of the Service.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Privacy</strong>. We care about the privacy of our Users.
              You understand that by using the Service you acknowledge and
              understand that your personal information will be collected, used,
              and disclosed as set forth in our{` `}
              <a href="https://www.someday.fm/privacy">Privacy Notice</a>. You
              also understand that your personal information will be collected,
              used, transferred to, and processed in the United States or any
              other country in which Someday or its parent, subsidiaries,
              affiliates, or service providers maintain facilities.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Security</strong>. Someday cares about the integrity and
              security of your information. However, we cannot guarantee that
              unauthorized third parties will never be able to defeat our
              security measures. You acknowledge that you provide your
              information at your own risk.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Text Messages</strong>. You expressly consent and agree
              that Someday can contact you using written, electronic, or verbal
              means, including by manual dialing, emails, leaving
              prerecorded/artificial voice messages or using an automatic
              telephone dialing system to call or text your mobile/cellular
              telephone number, as necessary to complete transactions requested
              by you and to service your account and as the law allows, even if
              those phone numbers are registered by you on any federal or state
              Do-Not-Call/Do-Not-email registry.
            </Paragraph>
            <Paragraph>
              We offer you the chance to enroll to receive recurring text
              messages from Someday. You may enroll to receive text messages
              about account-related news and alerts and/or marketing and
              promotional offers for Someday products and services. By enrolling
              in Someday’s text messaging service, you agree to receive text
              messages from Someday to your mobile phone number provided, and
              you certify that your mobile number provided is true and accurate
              and that you are authorized to enroll the designated mobile number
              to receive such texts. You acknowledge and agree that the texts
              may be sent using an automatic telephone dialing system and that
              standard message and data rates apply. Consent is not required as
              a condition of purchase. Someday is not responsible for any delays
              upon sending or receiving text messages.
            </Paragraph>
            <Paragraph>
              To unsubscribe from text messages at any time, reply STOP to any
              text message you receive from Someday. You consent that following
              such a request to unsubscribe, you may receive one final text
              message from Someday confirming your request. For help, reply HELP
              to any text message you receive from Someday or contact us at
              support@someday.fm.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Third-Party Links and Information</strong>. The Service
              may contain links to third-party materials that are not owned or
              controlled by Someday. Someday does not endorse or assume any
              responsibility for any such third-party sites, information,
              materials, products, or services. If you access a third-party
              website or service from the Service or share your User Content on
              or through any third-party website or service, you do so at your
              own risk, and you understand that these Terms and Someday’s
              Privacy Notice do not apply to your use of such sites. You
              expressly relieve Someday from any and all liability arising from
              your use of any third-party website, service, or content,
              including, without limitation, User Content submitted by other
              Users. Additionally, your dealings with or participation in
              promotions of advertisers found on the Service, including payment
              and delivery of goods, and any other terms (such as warranties)
              are solely between you and such advertisers. You agree that
              Someday shall not be responsible for any loss or damage of any
              sort relating to your dealings with such advertisers.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Release and Indemnity</strong>
            </Paragraph>
            <Paragraph>
              You hereby agree to release Someday from all damages (whether
              direct, indirect, incidental, consequential or otherwise), losses,
              liabilities, costs and expenses of every kind and nature, known
              and unknown, arising out of a dispute between you and a third
              party (including other Users) in connection with the Service.
            </Paragraph>
            <Paragraph>
              You agree to defend, indemnify and hold harmless Someday and its
              subsidiaries, agents, licensors, managers, and other affiliated
              companies, and their employees, contractors, agents, officers and
              directors, from and against any and all claims, damages,
              obligations, losses, liabilities, costs or debt, and expenses
              (including but not limited to attorneys’ fees) arising from: (i)
              your use of and access to the Service, including any data or
              content transmitted or received by you; (ii) your violation of any
              term of these Terms, including, without limitation, your breach of
              any of the representations and warranties above; (iii) your
              violation of any third-party right, including, without limitation,
              any right of privacy or Intellectual Property Rights; (iv) your
              interactions with other Users; (v) your violation of any
              applicable law, rule or regulation; (vi) User Content or any other
              content that is submitted via your User Account, including,
              without limitation, misleading, false, or inaccurate information;
              (vii) your negligence, recklessness or willful misconduct; or
              (viii) any other party’s access and/or use of the Service with
              your unique username, password or other appropriate security code.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>No Warranty</strong>
            </Paragraph>
            <Paragraph>
              The Service is provided on an “as is” and “as available” basis.
              Use of the Service is at your own risk. To the maximum extent
              permitted by applicable law, the Service is provided without
              warranties of any kind, whether express or implied, including, but
              not limited to, implied warranties of merchantability, fitness for
              a particular purpose, or non-infringement. No advice or
              information, whether oral or written, obtained by you from Someday
              or through the Service will create any warranty not expressly
              stated herein. Without limiting the foregoing, Someday, its
              subsidiaries, its affiliates, and its licensors do not warrant
              that the content is accurate, reliable or correct; that the
              Service will meet your requirements; that the Service will be
              available at any particular time or location, uninterrupted or
              secure; that any defects or errors will be corrected; or that the
              Service is free of viruses or other harmful components. Any
              content downloaded or otherwise obtained through the use of the
              Service is downloaded at your own risk and you will be solely
              responsible for any damage to your computer system or mobile
              device or loss of data that results from such download or your use
              of the Service.
            </Paragraph>
            <Paragraph>
              Further, Someday does not warrant, endorse, guarantee, or assume
              responsibility for any product or service advertised or offered by
              a third party through the Service or any hyperlinked website or
              service, and Someday will not be a party to or in any way monitor
              any transaction between you and third-party providers of products
              or services.
            </Paragraph>
            <Paragraph>
              Federal law, some states, provinces and other jurisdictions do not
              allow the exclusion and limitations of certain implied warranties,
              so the above exclusions may not apply to you. These Terms give you
              specific legal rights, and you may also have other rights which
              vary from state to state. The disclaimers and exclusions under
              these Terms will not apply to the extent prohibited by applicable
              law.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Limitation of Liability</strong>
            </Paragraph>
            <Paragraph>
              To the maximum extent permitted by applicable law, in no event
              shall Someday, its affiliates, agents, directors, employees,
              suppliers or licensors be liable for any indirect, punitive,
              incidental, special, consequential or exemplary damages,
              including, without limitation, damages for loss of profits,
              goodwill, use, data or other intangible losses, arising out of or
              relating to the use of, or inability to use, the Service. Under no
              circumstances will Someday be responsible for any damage, loss or
              injury resulting from hacking, tampering or other unauthorized
              access or use of the Service or your account or the information
              contained therein.
            </Paragraph>
            <Paragraph>
              To the maximum extent permitted by applicable law, Someday assumes
              no liability or responsibility for any (i) errors, mistakes, or
              inaccuracies of content; (ii) personal injury or property damage,
              of any nature whatsoever, resulting from your access to or use of
              the Service; (iii) any unauthorized access to or use of our secure
              servers and/or any and all personal information stored therein;
              (iv) any interruption or cessation of transmission to or from the
              Service; (v) any bugs, viruses, trojan horses, or the like that
              may be transmitted to or through the Service by any third party;
              (vi) any errors or omissions in any content or for any loss or
              damage incurred as a result of the use of any content posted,
              emailed, transmitted, or otherwise made available through the
              Service; and/or (vii) User Content, Feedback, or the defamatory,
              offensive, or illegal conduct of any third party. In no event
              shall Someday, its affiliates, agents, directors, employees,
              suppliers, or licensors be liable to you for any claims,
              proceedings, liabilities, obligations, damages, losses or costs in
              an amount exceeding the amount you paid to Someday hereunder or
              US$50.00, whichever is lesser.
            </Paragraph>
            <Paragraph>
              This limitation of liability section applies whether the alleged
              liability is based on contract, tort, negligence, strict
              liability, or any other basis, even if Someday has been advised of
              the possibility of such damage.
            </Paragraph>
            <Paragraph>
              Some states do not allow the exclusion or limitation of incidental
              or consequential damages, so the above limitations or exclusions
              may not apply to you. These Terms give you specific legal rights,
              and you may also have other rights which vary from state to state.
              The disclaimers, exclusions, and limitations of liability under
              these Terms will not apply to the extent prohibited by applicable
              law.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>
                Governing Law, Arbitration, and Class Action/Jury Trial Waiver
              </strong>
            </Paragraph>
            <ul>
              <ListItem>
                <Paragraph>
                  13.1. <strong>Governing Law.</strong> You agree that: (i) the
                  Service shall be deemed solely based in California; and (ii)
                  the Service shall be deemed a passive one that does not give
                  rise to personal jurisdiction over us, either specific or
                  general, in jurisdictions other than California. These Terms
                  shall be governed by the internal substantive laws of the
                  State of California, without respect to its conflict of laws
                  principles. The parties acknowledge that these Terms evidence
                  a transaction involving interstate commerce. Notwithstanding
                  the preceding sentences with respect to the substantive law,
                  the Federal Arbitration Act (9 U.S.C. §§ 1-16) (“
                  <strong>FAA</strong>”) governs the interpretation and
                  enforcement of the Arbitration Agreement and preempts all
                  state laws to the fullest extent permitted by law. If the FAA
                  is found to not apply to any issue that arises from or relates
                  to the Arbitration Agreement, then that issue shall be
                  resolved under and governed by the law of the State of
                  California. The application of the United Nations Convention
                  on Contracts for the International Sale of Goods is expressly
                  excluded. You agree to submit to the personal jurisdiction of
                  the federal and state courts located in Los Angeles County,
                  California for any actions for which we retain the right to
                  seek injunctive or other equitable relief in a court of
                  competent jurisdiction to prevent the actual or threatened
                  infringement, misappropriation or violation of a our
                  copyrights, trademarks, trade secrets, patents, or other
                  Intellectual Property Rights or proprietary rights, as set
                  forth in the Arbitration Agreement, including any provisional
                  relief required to prevent irreparable harm. You agree that
                  Los Angeles County, California is the proper and exclusive
                  forum for any appeals of an arbitration award or for trial
                  court proceedings in the event that the Arbitration Agreement
                  is found to be unenforceable.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  13.2. <strong>Arbitration.</strong> Read this section
                  carefully because it requires the parties to arbitrate their
                  disputes and limits the manner in which you can seek relief
                  from Someday. This Arbitration Agreement applies to and
                  governs any dispute, controversy, or claim between you and
                  Someday that arises out of or relates to, directly or
                  indirectly: (a) these Terms, including the formation,
                  existence, breach, termination, enforcement, interpretation,
                  validity, or enforceability thereof; (b) the Service,
                  including access to or use of the Service, as well as receipt
                  of any advertising, marketing, or other communications from
                  Someday; © any transactions through, by, or using the Service;
                  or (d) any other aspect of your relationship or transactions
                  with Someday (each, a “<strong>Claim</strong>,” and,
                  collectively, “<strong>Claims</strong>”). This Arbitration
                  Agreement shall apply, without limitation, to all Claims that
                  arose or were asserted before or after your acceptance of
                  these Terms.
                </Paragraph>
                <Paragraph>
                  If you are a new User, you can reject and opt-out of this
                  Arbitration Agreement within thirty (30) days of accepting
                  these Terms by emailing Someday at{` `}
                  <a href="mailto:support@someday.fm">support@someday.fm</a>
                  {` `}
                  with your full name and stating your intent to opt-out of this
                  Arbitration Agreement. Note that opting out of this
                  Arbitration Agreement does not affect any other part of these
                  Terms, including the provisions regarding governing law or in
                  which courts any disputes must be brought.
                </Paragraph>
                <Paragraph>
                  For any Claim, you agree to first contact us at{` `}
                  <a href="mailto:support@someday.fm">support@someday.fm</a>
                  {` `}
                  and to attempt to resolve the Claim with us informally. In the
                  unlikely event that Someday has not been able to resolve a
                  Claim it has with you within sixty (60) days, we each agree to
                  resolve such Claim exclusively through binding arbitration by
                  the American Arbitration Association (“
                  <strong>AAA</strong>”) before a single arbitrator (the “
                  <strong>Arbitrator</strong>”), under the Expedited Procedures
                  then in effect for AAA (the “<strong>Rules</strong>”), except
                  as otherwise provided herein, or as otherwise determined by
                  the Arbitrator. In the event of any conflict between the Rules
                  and this Arbitration Agreement, this Arbitration Agreement
                  shall control. AAA may be contacted at{` `}
                  <a href="http://www.adr.org">www.adr.org</a>, where the Rules
                  are also available. The arbitration will be conducted in Los
                  Angeles County, California, unless you and Someday agree
                  otherwise. If you are using the Service for commercial
                  purposes, each party will be responsible for paying any AAA
                  filing and administrative and Arbitrator fees in accordance
                  with the Rules, and the award rendered by the Arbitrator shall
                  include costs of arbitration, reasonable attorneys’ fees and
                  reasonable costs for expert and other witnesses. If you are an
                  individual using the Service for non-commercial purposes: (i)
                  AAA may require you to pay a fee for the initiation of your
                  case, unless you apply for and successfully obtain a fee
                  waiver from AAA; (ii) the award rendered by the Arbitrator may
                  include your costs of arbitration, your reasonable attorneys’
                  fees, and your reasonable costs for expert and other
                  witnesses; and (iii) you may sue in a small claims court of
                  competent jurisdiction, on an individual basis only, without
                  first engaging in arbitration, but this does not absolve you
                  of your commitment to engage in the informal dispute
                  resolution process. Any judgment on the award rendered by the
                  Arbitrator may be entered in any court of competent
                  jurisdiction. You and Someday agree that the Arbitrator, and
                  not any federal, state, or local court or agency, shall have
                  exclusive authority to resolve any disputes relating to the
                  scope, interpretation, applicability, enforceability, or
                  formation of this Arbitration Agreement, including any claim
                  that all or any part of this Arbitration Agreement is void or
                  voidable, or relating to any defense to arbitration, including
                  waiver, delay, laches, unconscionability, or estoppel. The
                  Arbitrator shall also be responsible for determining all
                  threshold arbitrability issues, including issues relating to
                  contract formation and whether the Terms, or any provision of
                  the Terms, are unconscionable or illusory.
                </Paragraph>
                <Paragraph>
                  Notwithstanding anything to the contrary, nothing in this
                  Arbitration Agreement shall be deemed as preventing Someday
                  from seeking injunctive or other equitable relief from the
                  courts as necessary to prevent the actual or threatened
                  infringement, misappropriation, or violation of its data
                  security, Intellectual Property Rights or other proprietary
                  rights; or as preventing you from asserting claims in a small
                  claims court, if your claims qualify and so long as the matter
                  remains in such court and advances on only an individual
                  (non-class, non-representative) basis.
                </Paragraph>
                <Paragraph>
                  If this Arbitration Agreement is found to be void,
                  unenforceable, or unlawful, in whole or in part, the void,
                  unenforceable, or unlawful provision, in whole or in part,
                  shall be severed. Severance of the void, unenforceable, or
                  unlawful provision, in whole or in part, shall have no impact
                  on the remaining provisions of this Arbitration Agreement,
                  which shall remain in force, or on the parties’ ability to
                  compel arbitration of any remaining Claims on an individual
                  basis pursuant to this Arbitration Agreement. Notwithstanding
                  the foregoing, if the Class Action/Jury Trial Waiver is found
                  to be void, unenforceable, or unlawful, in whole or in part,
                  because it would prevent you from seeking public injunctive
                  relief, then any dispute regarding the entitlement to such
                  relief (and only that relief) must be severed from arbitration
                  and may be litigated in a civil court of competent
                  jurisdiction. All other claims for relief subject to
                  arbitration under this Arbitration Agreement shall be
                  arbitrated under its terms, and the parties agree that
                  litigation of any dispute regarding the entitlement to public
                  injunctive relief shall be stayed pending the outcome of any
                  individual claims in arbitration.
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  13.3. <strong>Class Action/Jury Trial Waiver.</strong> With
                  respect to all persons and entities, regardless of whether
                  they have obtained or used the Service for personal,
                  commercial or other purposes, all Claims must be brought in
                  the parties’ individual capacity, and not as a plaintiff or
                  class member in any purported class action, collective action,
                  private attorney general action or other representative
                  proceeding. This mutual waiver applies to class arbitration,
                  and, unless we agree otherwise, the Arbitrator may not
                  consolidate more than one person’s Claims. You and Someday
                  mutually agree that the Arbitrator may award relief only to an
                  individual claimant and only to the extent necessary to
                  provide relief on your individual claim(s). Any relief awarded
                  may not affect other Users. You and Someday further agree
                  that, by entering into these Terms, you and Someday are each
                  waiving the right to a trial by jury or to bring, join, or
                  participate in a class action, collective action, private
                  attorney general action, or other representative proceeding of
                  any kind as a plaintiff or class member.
                </Paragraph>
              </ListItem>
            </ul>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>General</strong>
            </Paragraph>
            <ul>
              <ListItem>
                14.1. <strong>Assignment.</strong> These Terms, and any rights
                and licenses granted hereunder, may not be transferred or
                assigned by you, but may be assigned by Someday without
                restriction. Any attempted transfer or assignment in violation
                hereof shall be null and void.
              </ListItem>
              <ListItem>
                14.2.{` `}
                <strong>
                  Notification Procedures and Changes to these Terms.
                </strong>
                {` `}
                Someday may provide notifications, whether such notifications
                are required by law or are for marketing or other business
                related purposes, to you via email notice, written or hard copy
                notice, or through posting of such notice on our website, as
                determined by Someday in its sole discretion. Someday reserves
                the right to determine the form and means of providing
                notifications to Users, provided that you may opt out of certain
                means of notification as described in these Terms. Someday is
                not responsible for any automatic filtering you or your network
                provider may apply to email notifications we send to the email
                address you provide us. Someday may, in its sole discretion,
                modify or update these Terms from time to time, and so you
                should review this page periodically. When we change the Terms
                in a material manner, we will update the ‘last modified’ date at
                the top of this page and notify you that material changes have
                been made to the Terms. Your continued use of the Service after
                any such change constitutes your acceptance of the new Terms of
                Service. If you do not agree to any of the terms of these Terms
                or any future Terms of Service, do not access or use (or
                continue to access or use) the Service.
              </ListItem>
              <ListItem>
                14.3. <strong>Entire Agreement/Severability.</strong> These
                Terms, together with the Purchase Terms and Policies and any
                amendments and any additional agreements you may enter into with
                Someday in connection with the Service, shall constitute the
                entire agreement between you and Someday concerning the Service.
                Except as otherwise stated in the Arbitration Agreement, if any
                provision of these Terms is deemed invalid by a court of
                competent jurisdiction, the invalidity of such provision shall
                not affect the validity of the remaining provisions of these
                Terms, which shall remain in full force and effect, except that
                in the event of unenforceability of the Class Action/Jury Trial
                Waiver, the entire Arbitration Agreement shall be unenforceable.
              </ListItem>
              <ListItem>
                14.4. <strong>No Waiver.</strong> No waiver of any term of these
                Terms shall be deemed a further or continuing waiver of such
                term or any other term, and Someday’s failure to assert any
                right or provision under these Terms shall not constitute a
                waiver of such right or provision.
              </ListItem>
              <ListItem>
                14.5. <strong>Contact.</strong> Please contact us at{` `}
                <a href="mailto:support@someday.fm">support@someday.fm</a>
                {` `}
                with any questions regarding these Terms.
              </ListItem>
            </ul>
          </ListItem>
        </ol>
      </Paragraph>
    </div>
  );
};

const MobileTerms = (props: TermsProps) => {
  const {} = props;

  return <TermsContents {...props} />;
};

const DesktopTerms = (props: TermsProps) => {
  const {} = props;

  return (
    <div className={`mx-auto flex w-[1200px]`}>
      <TermsContents {...props} />
    </div>
  );
};

const Terms = (props: TermsProps) => {
  const {} = props;
  const viewport = useViewport();

  if (viewport == `mobile` || viewport == `tablet`) {
    return <MobileTerms {...props} />;
  }
  if (viewport == `desktop`) {
    return <DesktopTerms {...props} />;
  }

  return null;
};

export default Terms;
